import { observer } from 'mobx-react';
import * as React from 'react';

import { urls } from 'shared/entities/domain';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import {
  useCabinetsStore,
  useCabinetStore,
  useRouterStore,
  useUiStore
} from 'stores/index';
import { SelectorOptions } from 'shared/newEntities/components/Selector';
import loadGlobalStoresHOC from 'shared/HOCs/loadGlobalStoresHOC';

import BreadcrumbSelectorItem from '../BreadcrumbSelectorItem';

import '../Breadcrumbs.modules.scss';

type Props = {
  className?: string;
};

const CabinetItem: React.FC<Props> = ({ className }: Props) => {
  const routerStore = useRouterStore();
  const cabinetStore = useCabinetStore();
  const cabinetsStore = useCabinetsStore();
  const uiStore = useUiStore();

  const options = React.useMemo((): SelectorOptions => {
    const cabinets = [...cabinetsStore.cabinets.items];
    if (
      cabinetStore.entity &&
      !cabinetsStore.cabinets.items.find(
        (cabinet) => cabinet.domain === cabinetStore.entity?.domain
      )
    ) {
      cabinets.unshift(cabinetStore.entity);
    }

    return cabinets.reduce<SelectorOptions>(
      (acc, cabinet) => {
        return {
          ...acc,
          entities: {
            ...acc.entities,
            [cabinet.domain]: {
              id: cabinet.domain,
              title: cabinet.name
            }
          },
          keys: [...acc.keys, cabinet.domain]
        };
      },
      { keys: [], entities: {} }
    );
  }, [
    cabinetStore.entity,
    cabinetStore.entity?.domain,
    cabinetsStore.cabinets.items
  ]);

  const handleCabinetClick = React.useCallback(
    (domain) => {
      const isCurCabinet = cabinetStore.entity?.domain === domain;

      if (!isCurCabinet) {
        routerStore.changeSubDomain({
          subDomain: domain
        });
      } else {
        routerStore.push(urls.SETTINGS.root);
        if (uiStore.isMobileSize) {
          uiStore.menuState.close();
        }
      }
    },
    [cabinetStore.entity?.domain, uiStore.isMobileSize]
  );

  if (!cabinetStore.entity) {
    return null;
  }

  const isCabinetSettings = routerStore.matchPath(urls.SETTINGS.CABINET.mask);

  return (
    <BreadcrumbSelectorItem
      className={className}
      to={urls.SETTINGS.root}
      mask={urls.SETTINGS.mask}
      isActive={isCabinetSettings}
      options={options}
      value={cabinetStore.entity?.domain ?? null}
      onOptionClick={handleCabinetClick}
      loadingStage={cabinetsStore.loadingStage}
    >
      {cabinetStore.entity.name}
    </BreadcrumbSelectorItem>
  );
};

export default loadGlobalStoresHOC(
  observer(CabinetItem),
  ['cabinetsStore'],
  LoaderSpinnerSize.sm,
  LoaderSpinnerColor.white
);
